import {
	activePersonas,
	activeSegments,
	assignmentCount,
	findAssignment,
	isCriterionGroupActive,
	isLinked,
	personasOf,
	type PersonaView,
	type ScoreConfigInput,
	type ScoreScoringInput,
	segmentsOf,
	type SegmentView,
} from "./score-config-view";
import { unassignedSizeBucketLabels } from "./score-size-buckets";

export type ScoreIssueStep = "overview" | "general" | "company" | "prospect" | "scoring";

/**
 * Everything that stops the lead scoring from being complete, as one flat list
 * (PREC-4055). The tab badges, the hover panels, the error bars and the chip
 * status icons all read from this list, so "is this segment ok" has one answer.
 *
 * Pure: input is the score and its criteria, output is data. The message is an
 * i18n key plus params so the engine stays free of React and translation.
 */

export type ScoreIssueKind = "general" | "segment" | "persona" | "scoring" | "cell";

export type ScoreIssueKey =
	| "segmentMissingScoring"
	| "personaMissingScoring"
	| "segmentInactiveScoring"
	| "personaInactiveScoring"
	| "generalAboutMissing"
	| "segmentNoName"
	| "segmentNoDescription"
	| "segmentNoPersona"
	| "personaNoName"
	| "personaNoDescription"
	| "personaNoTitles"
	| "personaNotLinked"
	| "scoringNoValues"
	| "scoringAiEmpty"
	| "scoringNotApplied"
	| "cellMissing"
	| "scoringSizeUnassigned";

export interface ScoreIssueMessage {
	key: ScoreIssueKey;
	params?: Record<string, string | number>;
}

export interface ScoreIssue {
	step: ScoreIssueStep;
	kind: ScoreIssueKind;
	/** The segment key, persona id, scoring id — whatever the kind points at. */
	id: string;
	/** For `cell` (and segment/persona scoring gaps): the persona on the other axis. */
	personaId?: number;
	message: ScoreIssueMessage;
}

export interface ScoreIssueInput {
	score: ScoreConfigInput | null | undefined;
	criteria: ScoreScoringInput[];
}

/** Label keys (under `leadScoring.`) for the fixed criteria, used in scoring issues. */
export const CRITERION_LABEL_KEYS: Record<string, string> = {
	HQ_COUNTRY: "companyHQ",
	SUBSIDIARY_COUNTRY: "anyCompanyLocation",
	INDUSTRY: "industries",
	EMPLOYEE_SIZE: "employeeSize",
	PERSON_LOCATION: "personLocation",
	JOB_LEVEL: "seniority",
};

const MIN_DESCRIPTION_LENGTH = 4;

const hasText = (value: string | null | undefined): boolean =>
	(value ?? "").trim().length >= MIN_DESCRIPTION_LENGTH;

const nameOf = (row: { name: string }): string => row.name.trim();

const generalIssues = (score: ScoreConfigInput): ScoreIssue[] =>
	hasText(score.serviceDescription)
		? []
		: [
				{
					step: "general",
					kind: "general",
					id: "about",
					message: { key: "generalAboutMissing" },
				},
			];

const scoringGapIssue = (
	assignment: ReturnType<typeof findAssignment>,
	segment: SegmentView,
	persona: PersonaView,
	perspective: "segment" | "persona"
): ScoreIssue | null => {
	const base = {
		step: perspective === "segment" ? ("company" as const) : ("prospect" as const),
		kind: perspective,
		id: perspective === "segment" ? segment.key : `${persona.id}`,
		personaId: persona.id,
	};
	const params = { name: nameOf(segment), segment: nameOf(segment), persona: nameOf(persona) };

	if (!assignment) {
		return {
			...base,
			message: {
				key: perspective === "segment" ? "segmentMissingScoring" : "personaMissingScoring",
				params: perspective === "persona" ? { ...params, name: nameOf(persona) } : params,
			},
		};
	}

	if (!assignment.locked) {
		return {
			...base,
			message: {
				key: perspective === "segment" ? "segmentInactiveScoring" : "personaInactiveScoring",
				params: {
					...params,
					name: perspective === "persona" ? nameOf(persona) : nameOf(segment),
					scoring: assignment.criteriaName,
				},
			},
		};
	}

	return null;
};

const segmentIssues = (
	segment: SegmentView,
	personas: PersonaView[],
	criteria: ScoreScoringInput[]
): ScoreIssue[] => {
	const issues: ScoreIssue[] = [];
	const base = { step: "company" as const, kind: "segment" as const, id: segment.key };
	const name = nameOf(segment);

	if (segment.group !== "INVESTOR") {
		if (!name) {
			issues.push({ ...base, message: { key: "segmentNoName" } });
		}

		if (!hasText(segment.description)) {
			issues.push({ ...base, message: { key: "segmentNoDescription", params: { name } } });
		}
	}

	const linked = activePersonas(personas).filter((persona) => isLinked(persona, segment));

	if (linked.length === 0) {
		issues.push({ ...base, message: { key: "segmentNoPersona", params: { name } } });
	}

	for (const persona of linked) {
		const gap = scoringGapIssue(
			findAssignment(criteria, segment, persona.id),
			segment,
			persona,
			"segment"
		);

		if (gap) {
			issues.push(gap);
		}
	}

	return issues;
};

const personaIssues = (
	persona: PersonaView,
	segments: SegmentView[],
	criteria: ScoreScoringInput[]
): ScoreIssue[] => {
	const issues: ScoreIssue[] = [];
	const base = { step: "prospect" as const, kind: "persona" as const, id: `${persona.id}` };
	const name = nameOf(persona);

	if (!name) {
		issues.push({ ...base, message: { key: "personaNoName" } });
	}

	if (!hasText(persona.description)) {
		issues.push({ ...base, message: { key: "personaNoDescription", params: { name } } });
	}

	if (persona.jobsToInclude.length === 0) {
		issues.push({ ...base, message: { key: "personaNoTitles", params: { name } } });
	}

	const linked = activeSegments(segments).filter((segment) => isLinked(persona, segment));

	if (linked.length === 0) {
		issues.push({ ...base, message: { key: "personaNotLinked", params: { name } } });
	}

	for (const segment of linked) {
		const gap = scoringGapIssue(
			findAssignment(criteria, segment, persona.id),
			segment,
			persona,
			"persona"
		);

		if (gap) {
			issues.push(gap);
		}
	}

	return issues;
};

type CriterionGroup = { active?: boolean; items?: unknown[] };

const isOn = (group: CriterionGroup | undefined): boolean => isCriterionGroupActive(group);

const valuesOf = (items: unknown[] | undefined, field: string): string[] =>
	(items ?? []).flatMap((item) => {
		const values = (item as Record<string, unknown>)[field];

		return Array.isArray(values) ? (values as string[]) : [];
	});

/** The issues of one scoring's own grades — also what the editor's criterion chips show. */
export const criterionIssues = (scoring: ScoreScoringInput): ScoreIssue[] => {
	const issues: ScoreIssue[] = [];
	const base = { step: "scoring" as const, kind: "scoring" as const, id: `${scoring.id}` };
	const name = scoring.name;
	const { companyScoring, personaScoring } = scoring.criteria;

	const noValues = (criterion: string) =>
		issues.push({ ...base, message: { key: "scoringNoValues", params: { name, criterion } } });

	const listCriteria: [string, CriterionGroup | undefined, string][] = [
		["HQ_COUNTRY", companyScoring.HQ_COUNTRY, "countries"],
		["SUBSIDIARY_COUNTRY", companyScoring.SUBSIDIARY_COUNTRY, "countries"],
		["INDUSTRY", companyScoring.INDUSTRY, "industries"],
		["PERSON_LOCATION", personaScoring.PERSON_LOCATION, "countries"],
	];

	for (const [criterion, group, field] of listCriteria) {
		if (isOn(group) && valuesOf(group?.items, field).length === 0) {
			noValues(criterion);
		}
	}

	if (
		isOn(companyScoring.EMPLOYEE_SIZE) &&
		(companyScoring.EMPLOYEE_SIZE.items ?? []).length === 0
	) {
		noValues("EMPLOYEE_SIZE");
	}

	const size = companyScoring.EMPLOYEE_SIZE;
	if (scoring.locked && isOn(size) && (size.items ?? []).length > 0) {
		const labels = unassignedSizeBucketLabels(size.items ?? []);
		if (labels.length > 0) {
			issues.push({
				...base,
				message: {
					key: "scoringSizeUnassigned",
					params: {
						name,
						ranges: labels.join(", "),
						count: labels.length,
					},
				},
			});
		}
	}

	// A seniority level left out of every bucket scores nothing — a non-event, not an issue.
	if (isOn(personaScoring.JOB_LEVEL) && (personaScoring.JOB_LEVEL.items ?? []).length === 0) {
		noValues("JOB_LEVEL");
	}

	for (const other of [...companyScoring.OTHER, ...personaScoring.OTHER]) {
		if (
			isCriterionGroupActive(other) &&
			!other.criteria.some((bucket) => bucket.description.trim())
		) {
			issues.push({
				...base,
				message: { key: "scoringAiEmpty", params: { name, criterion: other.criteriaName } },
			});
		}
	}

	return issues;
};

const scoringIssues = (
	scoring: ScoreScoringInput,
	segments: SegmentView[],
	personas: PersonaView[]
): ScoreIssue[] => {
	const issues: ScoreIssue[] = [];

	if (assignmentCount(scoring, segments, personas) === 0) {
		issues.push({
			step: "scoring",
			kind: "scoring",
			id: `${scoring.id}`,
			message: { key: "scoringNotApplied", params: { name: scoring.name } },
		});
	}

	return [...issues, ...criterionIssues(scoring)];
};

const cellIssues = (
	segments: SegmentView[],
	personas: PersonaView[],
	criteria: ScoreScoringInput[]
): ScoreIssue[] => {
	const issues: ScoreIssue[] = [];

	for (const segment of activeSegments(segments)) {
		for (const persona of activePersonas(personas)) {
			if (isLinked(persona, segment) && !findAssignment(criteria, segment, persona.id)) {
				issues.push({
					step: "overview",
					kind: "cell",
					id: segment.key,
					personaId: persona.id,
					message: {
						key: "cellMissing",
						params: { segment: nameOf(segment), persona: nameOf(persona) },
					},
				});
			}
		}
	}

	return issues;
};

/** Every issue of the configuration, grouped by step in tab order. */
export const computeScoreIssues = ({ score, criteria }: ScoreIssueInput): ScoreIssue[] => {
	if (!score) {
		return [];
	}

	const segments = segmentsOf(score);
	const personas = personasOf(score);

	return [
		...cellIssues(segments, personas, criteria),
		...generalIssues(score),
		...activeSegments(segments).flatMap((segment) => segmentIssues(segment, personas, criteria)),
		...activePersonas(personas).flatMap((persona) => personaIssues(persona, segments, criteria)),
		// A deactivated scoring grades nothing, so it has nothing to fix before scoring can run.
		...criteria
			.filter((scoring) => scoring.locked)
			.flatMap((scoring) => scoringIssues(scoring, segments, personas)),
	];
};

export const issuesForStep = (issues: ScoreIssue[], step: string): ScoreIssue[] =>
	issues.filter((issue) => issue.step === step);

/** The issues that point at one entity — a chip's status icon and its hover list. */
export const issuesFor = (issues: ScoreIssue[], kind: ScoreIssueKind, id: string): ScoreIssue[] =>
	issues.filter((issue) => issue.kind === kind && issue.id === id);
